/** @format */

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react'
import styled from 'styled-components'
import {
  _$,
  Drawer,
  Form,
  FormItem,
  Input,
  Message,
  Editable,
  Button,
  Select,
  Option
} from '@king-design/react'
import { ContactDrawerProps, UpdateUserProps, initConstactsForm } from './constants'
import { CountContext } from 'src/pages/MonitorContacts'
import { phoneValidate } from 'src/config'
import API from 'src/api/ApiContacts'
import { request } from 'src/utils'
import _ from 'lodash'
const Contacts: React.FC<ContactDrawerProps> = ({ className, visible, closeDrawer, curUser }) => {
  const { groupList } = useContext(CountContext)
  const [formValues, setFormValues] = useState<UpdateUserProps>(initConstactsForm)
  const [codeLoading, setCodeLoading] = useState<boolean>(false)
  const [codeSecond, setCodeSecond] = useState<number>(60)
  const timerRef = useRef<NodeJS.Timeout>()
  const formRef = useRef<any>()
  // 初始化
  useEffect(() => {
    if (visible) {
      // 打开：获取联系组(新建)、获取初始值(修改)
      if (curUser.userid) {
        const { userid, name, email, phone, usergroup } = curUser
        setFormValues({
          Id: userid,
          Name: name,
          Email: email,
          Phone: phone,
          ContactGroupIds: usergroup?.length ? usergroup.map(({ usrgrpid }) => usrgrpid) : []
        })
      }
    } else {
      // 关闭：清空form、清空联系组list、关闭定时器、取消验证码发送等待、重置秒数
      setFormValues(initConstactsForm)
      timerRef?.current && clearInterval(timerRef.current)
      setCodeLoading(false)
      setCodeSecond(60)
    }
    return () => {
      timerRef?.current && clearInterval(timerRef.current)
    }
  }, [visible])

  // 修改formValues
  const changeFormValues = values => {
    setFormValues({
      ...formValues,
      ...values
    })
  }

  // 是否可以获取验证码
  const ifChangePhone = useMemo(() => {
    const { Phone } = formValues
    if (Phone && phoneValidate.validate.test(Phone) && Phone !== curUser.phone) {
      return true
    } else {
      return false
    }
  }, [formValues.Phone])

  useEffect(() => {
    clearInterval(timerRef.current)
    if (codeLoading) {
      timerRef.current = setInterval(() => {
        setCodeSecond(pre => {
          if (pre <= 1) {
            setCodeLoading(false)
            timerRef.current && clearInterval(timerRef.current)
            // 重置秒数
            return 60
          }
          return pre - 1
        })
      }, 1000)
    } else {
    }
  }, [codeLoading])

  // 获取验证码
  const getVerifyCode = async () => {
    if (ifChangePhone) {
      const result = await request(API.getVerifyCode, {
        method: 'POST',
        data: {
          phone: formValues.Phone
        }
      })
      if (result.code === 'Success') {
        setCodeLoading(true)
        Message.success(result?.['message'] || _$('验证码发送成功'))
      }
    }
  }

  // 提交
  const formSubmit = async () => {
    const validate = await formRef?.current?.validate()
    if (validate) {
      const params = _.cloneDeep(formValues)
      // 没修改手机号的话不传phone
      !ifChangePhone && delete params.Phone
      // 区分创建/修改
      const api = curUser.userid ? API.updateAlertUser : API.createAlertUser
      const result = await request(api, {
        method: 'POST',
        data: {
          ...params
        }
      })
      if (result.code === 'Success') {
        Message.success(result?.['message'])
        setCodeLoading(true)
        closeDrawer(true)
      }
    }
  }

  return (
    <Drawer
      width={600}
      value={visible}
      title={curUser?.userid ? _$('修改联系人') : _$('新建联系人')}
      onClose={() => {
        closeDrawer()
      }}
      cancel={() => {
        closeDrawer()
      }}
      ok={() => {
        formSubmit()
      }}
    >
      <div className={className}>
        <Form starOnRequired={true} ref={formRef} labelWidth={100}>
          <FormItem
            label={_$('姓名')}
            value={formValues.Name}
            rules={{
              required: true,
              userName: true
            }}
          >
            <Input
              value={formValues.Name}
              onChangeValue={value => {
                changeFormValues({
                  Name: value
                })
              }}
            />
          </FormItem>
          <FormItem
            label={_$('邮箱')}
            value={formValues.Email}
            rules={{
              required: true,
              email: true
            }}
          >
            <Input
              value={formValues.Email}
              onChangeValue={value => {
                changeFormValues({
                  Email: value
                })
              }}
            />
          </FormItem>
          <FormItem
            label={_$('手机号')}
            value={formValues.Phone}
            rules={{
              required: true,
              phone: true
            }}
          >
            {formValues.Id ? (
              <Editable
                value={formValues.Phone}
                onChangeValue={value => {
                  changeFormValues({
                    Phone: value
                  })
                }}
              >
                {formValues.Phone}
              </Editable>
            ) : (
              <Input
                value={formValues.Phone}
                onChangeValue={value => {
                  changeFormValues({
                    Phone: value
                  })
                }}
              />
            )}
          </FormItem>
          {ifChangePhone ? (
            <FormItem
              label={_$('验证码')}
              value={formValues.VerifyCode}
              rules={{
                required: true,
                verifyCode: true
              }}
            >
              <Input
                value={formValues.VerifyCode}
                onChangeValue={value => {
                  changeFormValues({
                    VerifyCode: value
                  })
                }}
                slotAppend={
                  <Button disabled={codeLoading} type="primary" onClick={getVerifyCode}>
                    {codeLoading ? `${codeSecond}${_$('秒后重发')}` : _$('发送验证码')}
                  </Button>
                }
              />
            </FormItem>
          ) : null}
          {!formValues.Id ? (
            <FormItem
              label={_$('联系组')}
              value={formValues.ContactGroupIds}
              messages={{
                required: _$('请选择联系组')
              }}
            >
              <Select
                value={formValues.ContactGroupIds}
                multiple
                onChangeValue={value => {
                  changeFormValues({
                    ContactGroupIds: value
                  })
                }}
              >
                {groupList?.length &&
                  groupList.map(({ name, usrgrpid }) => (
                    <Option key={usrgrpid} value={usrgrpid}>
                      {name}
                    </Option>
                  ))}
              </Select>
            </FormItem>
          ) : null}
        </Form>
      </div>
    </Drawer>
  )
}
export default styled(Contacts)`
  .k-form-item {
    width: 380px;
  }
  .k-editable {
    line-height: 32px;
  }
  p {
    margin-bottom: 0;
  }
`
